import {
	canUserInvite,
	getConfluencePermittedUsers,
	getInviteesWithoutJiraAccess,
	getUsersFromCollaborationGraph,
} from '@atlassian/invite-prompt';
import type { Collaborators, Invitees } from '@atlassian/invite-prompt';

import { fg } from '@confluence/feature-gating';

export type IneligibilityReason =
	| 'NOT_ELIGIBLE_FOR_INVITE'
	| 'NO_COLLABORATORS_FOUND'
	| 'COLLABORATORS_ALL_HAVE_ACCESS'
	| 'COLLABORATORS_HAVE_NO_JIRA_ACCESS';

export type ResolverInput = {
	userId: string;
	cloudId: string;
};

const PRODUCT = 'confluence';

export type ResolverResult =
	| {
			ineligibilityReason: IneligibilityReason;
	  }
	| {
			ineligibilityReason: null;
			collaborators: Collaborators;
	  };

export const resolveInviteOnPublish = async ({
	userId,
	cloudId,
}: ResolverInput): Promise<ResolverResult> => {
	const collaborators = await getUsersFromCollaborationGraph(cloudId, userId);
	if (collaborators.length === 0) {
		return {
			ineligibilityReason: 'NO_COLLABORATORS_FOUND',
		};
	}

	let inviteesWithoutJiraAccess: Invitees = [];
	if (fg('icopp_check_jira_access')) {
		inviteesWithoutJiraAccess = await getInviteesWithoutJiraAccess(
			cloudId,
			collaborators.map(({ id, name, avatar }) => ({ id, name, avatar })),
		);
	}
	if (inviteesWithoutJiraAccess.length === collaborators.length) {
		return {
			ineligibilityReason: 'COLLABORATORS_HAVE_NO_JIRA_ACCESS',
		};
	}

	const collaboratorsIdsWithoutJiraAccess = inviteesWithoutJiraAccess.map((invitee) => invitee.id);

	const collaboratorsWithJiraAccess = collaborators.filter(
		(c) => !collaboratorsIdsWithoutJiraAccess.includes(c.id),
	);

	const collaboratorsIdsWithConfluenceAccess = await getConfluencePermittedUsers(
		cloudId,
		collaboratorsWithJiraAccess.map((c) => c.id),
	);

	const collaboratorsWithoutConfluenceAccess = collaboratorsWithJiraAccess.filter(
		(c) => !collaboratorsIdsWithConfluenceAccess.includes(c.id),
	);

	if (collaboratorsWithoutConfluenceAccess.length === 0) {
		return {
			ineligibilityReason: 'COLLABORATORS_ALL_HAVE_ACCESS',
		};
	}

	if (!(await canUserInvite(cloudId, PRODUCT))) {
		return {
			ineligibilityReason: 'NOT_ELIGIBLE_FOR_INVITE',
		};
	}

	return {
		ineligibilityReason: null,
		collaborators: collaboratorsWithoutConfluenceAccess,
	};
};
